
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Building, Clock } from "lucide-react";
import { getTimeInStage } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Candidate } from "../types";

interface CandidateInfoProps {
  candidate: Candidate;
}

export const CandidateInfo = ({ candidate }: CandidateInfoProps) => {
  const getInitials = (firstName: string | null, lastName: string | null) => {
    return `${(firstName?.[0] || "").toUpperCase()}${(lastName?.[0] || "").toUpperCase()}`;
  };

  const timeInStage = getTimeInStage(candidate.updated_at);
  const isStale = timeInStage.includes("week") || timeInStage.includes("month");

  return (
    <div className="flex items-start gap-3">
      <Avatar className="h-10 w-10">
        <AvatarFallback className="bg-primary/10 text-primary">
          {getInitials(candidate.first_name, candidate.last_name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="font-medium group-hover:text-primary transition-colors truncate text-foreground">
          {candidate.first_name} {candidate.last_name}
        </div>
        
        {candidate.email && (
          <div className="text-xs text-muted-foreground truncate">
            {candidate.email}
          </div>
        )}
        
        {candidate.assigned_posting && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Building className="h-3 w-3" />
            <span className="truncate">{candidate.assigned_posting.company || "No company"}</span>
          </div>
        )}
        
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className={cn("h-3 w-3", isStale ? "text-amber-500" : "")} />
          {isStale ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={cn("cursor-help", isStale ? "text-amber-500 font-medium" : "")}>
                    In stage: {timeInStage}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  This candidate hasn't had any activity in over 14 days
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span>In stage: {timeInStage}</span>
          )}
        </div>
        
        {candidate.assigned_posting && (
          <div className="pt-1">
            <Badge variant="secondary" className="text-xs truncate">
              {candidate.assigned_posting.title}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
};
